'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import type { Employee, EmployeeTimeOffBalance } from './types';

interface BalanceAllocationModalProps {
  mode: 'edit' | 'create' | 'grant' | null;
  employee: Employee | null;
  existingBalance: EmployeeTimeOffBalance | null;
  excludeBalanceTypes?: string[];
  onClose: () => void;
  onSaved: () => void;
}

const BALANCE_LABELS: Record<string, { label: string; color: string }> = {
  pto:               { label: 'Paid Time Off',    color: '#E89A3C' },
  sick:              { label: 'Sick Time',        color: '#dc2626' },
  bereavement:       { label: 'Bereavement',      color: '#6b7280' },
  jury_duty:         { label: 'Jury Duty',        color: '#7c3aed' },
  parental:          { label: 'Parental Leave',   color: '#16a34a' },
  floating_holiday:  { label: 'Floating Holiday', color: '#F5A623' },
  unpaid:            { label: 'Unpaid Leave',     color: '#6b7280' },
  other:             { label: 'Other',            color: '#6b7280' },
};

const ALL_BALANCE_TYPES = Object.keys(BALANCE_LABELS);

const BalanceAllocationModal = ({ mode, employee, existingBalance, excludeBalanceTypes = [], onClose, onSaved }: BalanceAllocationModalProps) => {
  // Edit/Create state
  const [hoursAccrued, setHoursAccrued] = useState(0);
  const [hoursPerYear, setHoursPerYear] = useState<number | null>(null);
  const [carryoverLimit, setCarryoverLimit] = useState<number | null>(null);
  const [resetDate, setResetDate] = useState('');
  const [showAccruedWarning, setShowAccruedWarning] = useState(false);
  // Create-mode only
  const [newBalanceType, setNewBalanceType] = useState<string>('');
  // Grant-mode only
  const [grantHours, setGrantHours] = useState(8);
  const [grantReason, setGrantReason] = useState('');
  // Common
  const [submitting, setSubmitting] = useState(false);

  const supabase = createClient();

  // Reset state on open
  useEffect(() => {
    if (!mode) return;
    setSubmitting(false);
    setShowAccruedWarning(false);

    if (mode === 'edit' && existingBalance) {
      setHoursAccrued(Number(existingBalance.hours_accrued));
      setHoursPerYear(existingBalance.hours_per_year);
      setCarryoverLimit(existingBalance.carryover_limit_hours);
      setResetDate(existingBalance.reset_date ?? '');
    } else if (mode === 'create') {
      setHoursAccrued(0);
      setHoursPerYear(null);
      setCarryoverLimit(null);
      setResetDate('');
      const available = ALL_BALANCE_TYPES.filter(t => !excludeBalanceTypes.includes(t));
      setNewBalanceType(available[0] ?? '');
    } else if (mode === 'grant' && existingBalance) {
      setGrantHours(8);
      setGrantReason('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, existingBalance?.id, excludeBalanceTypes.join(',')]);

  useEffect(() => {
    if (!mode) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [mode, onClose]);

  if (!mode || !employee) return null;

  // ============================================================
  // EDIT MODE
  // ============================================================
  if (mode === 'edit') {
    if (!existingBalance) return null;
    const cfg = BALANCE_LABELS[existingBalance.balance_type] ?? BALANCE_LABELS.other;
    const currentlyUsed = Number(existingBalance.hours_used ?? 0);
    const wouldGoNegative = hoursAccrued < currentlyUsed;
    const newRemaining = Math.max(0, hoursAccrued - currentlyUsed);
    const accruedDelta = hoursAccrued - Number(existingBalance.hours_accrued);
    const isValid = hoursAccrued >= 0 && !submitting && !wouldGoNegative;

    const saveEdit = async () => {
      if (!isValid) return;
      if (!showAccruedWarning && accruedDelta !== 0) {
        setShowAccruedWarning(true);
        return;
      }
      setSubmitting(true);
      const payload: Record<string, unknown> = {
        hours_accrued: hoursAccrued,
        hours_per_year: hoursPerYear,
        carryover_limit_hours: carryoverLimit,
        reset_date: resetDate || null,
        updated_at: new Date().toISOString(),
      };
      console.log(`[BalanceAlloc] Edit ${existingBalance.id}:`, payload);
      for (let attempt = 0; attempt < 3; attempt++) {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`Save timed out (attempt ${attempt + 1}/3)`)), 8000));
        const opPromise = supabase.from('employee_time_off_balances').update(payload).eq('id', existingBalance.id)
          .then((res: { error: any; data: any }) => { if (res.error) throw res.error; return res.data; });
        try {
          await Promise.race([opPromise, timeoutPromise]);
          toast.success(`${cfg.label} allocation updated for ${employee.first_name}`, { duration: 3000, icon: '⚙️' });
          onSaved();
          setSubmitting(false);
          onClose();
          return;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
          const isTimeout = msg.includes('timed out');
          console.warn(`[BalanceAlloc] Edit attempt ${attempt + 1} failed: ${msg}`);
          if (attempt === 2 || (!isRateLimit && !isTimeout)) {
            toast.error(`Save failed: ${msg}`, { duration: 6000 });
            setSubmitting(false);
            return;
          }
          await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
        }
      }
    };

    const archiveBalance = async () => {
      if (currentlyUsed > 0) {
        const confirmMsg = `${employee.first_name}'s ${cfg.label} balance has ${currentlyUsed}h already used. Archiving will hide this balance from active UI but preserve audit history of past requests. Continue?`;
        if (!confirm(confirmMsg)) return;
      }
      setSubmitting(true);
      console.log(`[BalanceAlloc] Archive ${existingBalance.id}`);
      for (let attempt = 0; attempt < 3; attempt++) {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`Archive timed out (attempt ${attempt + 1}/3)`)), 8000));
        const opPromise = supabase.from('employee_time_off_balances')
          .update({ is_archived: true, updated_at: new Date().toISOString() })
          .eq('id', existingBalance.id)
          .then((res: { error: any; data: any }) => { if (res.error) throw res.error; return res.data; });
        try {
          await Promise.race([opPromise, timeoutPromise]);
          toast(`${cfg.label} archived for ${employee.first_name}`, { duration: 3000, icon: '📦' });
          onSaved();
          setSubmitting(false);
          onClose();
          return;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
          const isTimeout = msg.includes('timed out');
          console.warn(`[BalanceAlloc] Archive attempt ${attempt + 1} failed: ${msg}`);
          if (attempt === 2 || (!isRateLimit && !isTimeout)) {
            toast.error(`Archive failed: ${msg}`, { duration: 6000 });
            setSubmitting(false);
            return;
          }
          await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
        }
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-start justify-between p-5 border-b border-gray-100">
            <div>
              <h3 className="text-lg font-bold text-[#1C2E5E]">Edit {cfg.label} Allocation</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">{employee.first_name} {employee.last_name}</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" /></svg>
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="rounded-xl p-4" style={{ background: `${cfg.color}10`, border: `1px solid ${cfg.color}30` }}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Current State</p>
              <div className="flex items-baseline gap-3 flex-wrap">
                <div>
                  <span className="text-2xl font-bold" style={{ color: cfg.color }}>{Number(existingBalance.hours_accrued).toFixed(1)}h</span>
                  <span className="text-[10px] text-gray-500 ml-1">accrued</span>
                </div>
                <div>
                  <span className="text-lg font-bold text-gray-700">{currentlyUsed.toFixed(1)}h</span>
                  <span className="text-[10px] text-gray-500 ml-1">used</span>
                </div>
                <div>
                  <span className="text-lg font-bold" style={{ color: cfg.color }}>{Number(existingBalance.hours_remaining).toFixed(1)}h</span>
                  <span className="text-[10px] text-gray-500 ml-1">remaining</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Total Hours Accrued <span className="text-red-500">*</span></label>
              <input type="number" min="0" step="0.5" value={hoursAccrued} onChange={e => { setHoursAccrued(Number(e.target.value)); setShowAccruedWarning(false); }} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
              {wouldGoNegative && <p className="text-[11px] text-red-600 font-bold mt-1">⚠ Cannot reduce below already-used hours ({currentlyUsed}h).</p>}
              {accruedDelta !== 0 && !wouldGoNegative && <p className="text-[11px] text-gray-600 mt-1">{accruedDelta > 0 ? '↑' : '↓'} {Math.abs(accruedDelta).toFixed(1)}h change · new remaining will be {newRemaining.toFixed(1)}h</p>}
            </div>

            {showAccruedWarning && accruedDelta !== 0 && (
              <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
                <p className="text-[11px] font-bold text-amber-900 mb-1">⚠ Changing accrued hours directly</p>
                <p className="text-[11px] text-amber-800 leading-relaxed">For tracked additions, prefer &quot;Grant Time Off&quot; instead. Click &quot;Save Anyway&quot; to confirm direct edit.</p>
              </div>
            )}

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Annual Accrual Rate (hours/year)</label>
              <input type="number" min="0" step="0.5" value={hoursPerYear ?? ''} onChange={e => setHoursPerYear(e.target.value ? Number(e.target.value) : null)} placeholder="e.g. 120 (15 days/year)" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Carryover Limit (hours)</label>
              <input type="number" min="0" step="0.5" value={carryoverLimit ?? ''} onChange={e => setCarryoverLimit(e.target.value ? Number(e.target.value) : null)} placeholder="e.g. 40" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Reset Date</label>
              <input type="date" value={resetDate} onChange={e => setResetDate(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>

            <div className="pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={archiveBalance}
                disabled={submitting}
                className="w-full px-4 py-2 rounded-lg text-[11px] font-bold text-red-700 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                Archive this balance type
              </button>
              <p className="text-[10px] text-gray-400 mt-1.5 text-center">Soft-delete: hidden from UI but past requests stay linked.</p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
            <button type="button" onClick={saveEdit} disabled={!isValid} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#1C2E5E] hover:bg-[#022b80] disabled:opacity-50 transition-colors">
              {submitting ? 'Saving…' : showAccruedWarning && accruedDelta !== 0 ? 'Save Anyway' : 'Save Allocation'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // CREATE MODE
  // ============================================================
  if (mode === 'create') {
    const availableTypes = ALL_BALANCE_TYPES.filter(t => !excludeBalanceTypes.includes(t));
    const cfg = BALANCE_LABELS[newBalanceType] ?? BALANCE_LABELS.other;
    const isValid = !!newBalanceType && hoursAccrued >= 0 && !submitting && availableTypes.length > 0;

    const createBalance = async () => {
      if (!isValid) return;
      setSubmitting(true);
      const payload: Record<string, unknown> = {
        employee_id: employee.id,
        balance_type: newBalanceType,
        hours_accrued: hoursAccrued,
        hours_used: 0,
        hours_per_year: hoursPerYear,
        carryover_limit_hours: carryoverLimit,
        reset_date: resetDate || null,
        is_archived: false,
      };
      console.log(`[BalanceAlloc] Create:`, payload);
      for (let attempt = 0; attempt < 3; attempt++) {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`Create timed out (attempt ${attempt + 1}/3)`)), 8000));
        const opPromise = supabase.from('employee_time_off_balances').insert(payload)
          .then((res: { error: any; data: any }) => { if (res.error) throw res.error; return res.data; });
        try {
          await Promise.race([opPromise, timeoutPromise]);
          toast.success(`${cfg.label} allocation added for ${employee.first_name}`, { duration: 3000, icon: '➕' });
          onSaved();
          setSubmitting(false);
          onClose();
          return;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
          const isTimeout = msg.includes('timed out');
          console.warn(`[BalanceAlloc] Create attempt ${attempt + 1} failed: ${msg}`);
          if (attempt === 2 || (!isRateLimit && !isTimeout)) {
            toast.error(`Create failed: ${msg}`, { duration: 6000 });
            setSubmitting(false);
            return;
          }
          await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
        }
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-start justify-between p-5 border-b border-gray-100">
            <div>
              <h3 className="text-lg font-bold text-[#1C2E5E]">Add Balance Type</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">{employee.first_name} {employee.last_name}</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" /></svg>
            </button>
          </div>

          <div className="p-5 space-y-4">
            {availableTypes.length === 0 ? (
              <p className="text-[12px] text-gray-500 italic text-center py-6">All balance types are already configured for this employee.</p>
            ) : (
              <>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Balance Type <span className="text-red-500">*</span></label>
                  <select value={newBalanceType} onChange={e => setNewBalanceType(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all">
                    {availableTypes.map(t => <option key={t} value={t}>{BALANCE_LABELS[t]?.label ?? t}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Initial Hours Accrued <span className="text-red-500">*</span></label>
                  <input type="number" min="0" step="0.5" value={hoursAccrued} onChange={e => setHoursAccrued(Number(e.target.value))} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
                  <p className="text-[10px] text-gray-400 mt-1">Hours available immediately upon creation.</p>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Annual Accrual Rate (hours/year)</label>
                  <input type="number" min="0" step="0.5" value={hoursPerYear ?? ''} onChange={e => setHoursPerYear(e.target.value ? Number(e.target.value) : null)} placeholder="e.g. 24 (3 days/year)" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Carryover Limit</label>
                  <input type="number" min="0" step="0.5" value={carryoverLimit ?? ''} onChange={e => setCarryoverLimit(e.target.value ? Number(e.target.value) : null)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Reset Date</label>
                  <input type="date" value={resetDate} onChange={e => setResetDate(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
            {availableTypes.length > 0 && (
              <button type="button" onClick={createBalance} disabled={!isValid} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white transition-colors disabled:opacity-50" style={{ background: cfg.color }}>
                {submitting ? 'Adding…' : 'Add Balance Type'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // GRANT MODE
  // ============================================================
  if (mode === 'grant') {
    if (!existingBalance) return null;
    const cfg = BALANCE_LABELS[existingBalance.balance_type] ?? BALANCE_LABELS.other;
    const newAccrued = Number(existingBalance.hours_accrued) + grantHours;
    const newRemaining = newAccrued - Number(existingBalance.hours_used ?? 0);
    const isValid = grantHours > 0 && grantReason.trim().length >= 3 && !submitting;

    const grantBonus = async () => {
      if (!isValid) return;
      setSubmitting(true);
      const grantNote = `[${new Date().toISOString().slice(0, 10)}] +${grantHours}h: ${grantReason.trim()}`;
      const existingNotes = (existingBalance as any).notes ?? '';
      const newNotes = existingNotes ? `${existingNotes}\n${grantNote}` : grantNote;
      const payload: Record<string, unknown> = {
        hours_accrued: newAccrued,
        notes: newNotes,
        updated_at: new Date().toISOString(),
      };
      console.log(`[BalanceAlloc] Grant ${grantHours}h to ${existingBalance.id}:`, payload);
      for (let attempt = 0; attempt < 3; attempt++) {
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(`Grant timed out (attempt ${attempt + 1}/3)`)), 8000));
        const opPromise = supabase.from('employee_time_off_balances').update(payload).eq('id', existingBalance.id)
          .then((res: { error: any; data: any }) => { if (res.error) throw res.error; return res.data; });
        try {
          await Promise.race([opPromise, timeoutPromise]);
          toast.success(`Granted ${grantHours}h ${cfg.label} to ${employee.first_name}`, { duration: 3500, icon: '🎁' });
          onSaved();
          setSubmitting(false);
          onClose();
          return;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
          const isTimeout = msg.includes('timed out');
          console.warn(`[BalanceAlloc] Grant attempt ${attempt + 1} failed: ${msg}`);
          if (attempt === 2 || (!isRateLimit && !isTimeout)) {
            toast.error(`Grant failed: ${msg}`, { duration: 6000 });
            setSubmitting(false);
            return;
          }
          await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
        }
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-start justify-between p-5 border-b border-gray-100">
            <div>
              <h3 className="text-lg font-bold text-[#1C2E5E]">Grant {cfg.label}</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">{employee.first_name} {employee.last_name}</p>
            </div>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" /></svg>
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="rounded-xl p-4" style={{ background: `${cfg.color}10`, border: `1px solid ${cfg.color}30` }}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">Current</p>
              <div className="flex items-baseline gap-3">
                <span className="text-2xl font-bold" style={{ color: cfg.color }}>{Number(existingBalance.hours_accrued).toFixed(1)}h</span>
                <span className="text-[10px] text-gray-500">accrued · {Number(existingBalance.hours_remaining).toFixed(1)}h remaining</span>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Hours to Grant <span className="text-red-500">*</span></label>
              <input type="number" min="0.5" step="0.5" value={grantHours} onChange={e => setGrantHours(Number(e.target.value))} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
              {grantHours > 0 && (
                <p className="text-[11px] text-gray-600 mt-1">↑ New balance: {newAccrued.toFixed(1)}h accrued · {newRemaining.toFixed(1)}h remaining</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Reason <span className="text-red-500">*</span></label>
              <textarea value={grantReason} onChange={e => setGrantReason(e.target.value)} rows={2} maxLength={300} placeholder="e.g. Holiday bonus week, comp time for weekend work" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all resize-none" />
              <p className="text-[10px] text-gray-400 mt-1">Logged in audit notes. Min 3 characters.</p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
            <button type="button" onClick={grantBonus} disabled={!isValid} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white transition-colors disabled:opacity-50" style={{ background: '#16a34a' }}>
              {submitting ? 'Granting…' : `Grant ${grantHours}h`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BalanceAllocationModal;
